/**
 * Per-element session-replay privacy markers — the single source of truth for BOTH
 * telemetry vendors (APX-672).
 *
 * Datadog RUM runs fail-closed at `defaultPrivacyLevel: 'mask'` (src/app/datadog-init.tsx),
 * so every rendered string is censored unless an ancestor opts it back in. These constants
 * are how a surface opts in — and, just as importantly, how a PII-bearing child inside an
 * opted-in container opts back OUT.
 *
 * WHY A CLASS STRING RATHER THAN A `data-*` PROP:
 *
 * 1. Datadog honours the class form and the attribute form identically —
 *    `getPrivacySelector` in @datadog/browser-rum-core/cjs/domain/privacyConstants.js builds
 *    `[data-dd-privacy="<level>"], .dd-privacy-<level>`. FullStory's only code-side lever is
 *    a class. One class string therefore carries both vendors, which is the only thing that
 *    keeps the two allowlists from drifting apart over time.
 * 2. `className` reaches every chrome component in this repo. Arbitrary `data-*` reaches only
 *    the ones that spread `...rest`, which many shared components do not.
 *
 * WHY NOT A WRAPPER COMPONENT: it would insert a DOM node into flex and table layouts, and
 * replay layout fidelity is an acceptance criterion of APX-672. It would also not work
 * through portals — the recorder walks the DOM, not the React tree, so a portal's node is a
 * child of `body` and never sees a React ancestor's marker.
 *
 * FULLSTORY STATUS: the `fs-*` tokens are INERT today. FullStory captures everything by
 * default and its global fail-closed switch is an org-console setting with no counterpart in
 * `SnippetOptions` (@fullstory/browser/dist/index.d.ts). Shipping the tokens now costs
 * nothing and means the console flip needs no second sweep through these components.
 *
 * Do not write a bare `dd-privacy-*` or `fs-*` literal anywhere else — import from here, so
 * the unmask surface stays greppable and reviewable in one place.
 */

/**
 * Opt a subtree back IN to recording. Use ONLY on application chrome whose content cannot
 * carry personal data: navigation, static copy, enum-backed status labels, column headers.
 *
 * The marker is INHERITED by the whole subtree, so before applying it ask what a caller can
 * render inside. A generic `children` container is not a safe place for it.
 */
export const TELEMETRY_UNMASK = 'dd-privacy-allow fs-unmask';

/**
 * Force a subtree back OUT of recording. Needed because {@link TELEMETRY_UNMASK} inherits:
 * an unmasked chrome region that renders one dynamic, person-derived value must re-mask it
 * explicitly. The trailing crumb in the Settings breadcrumb is the canonical example — it is
 * a real person's name rendered inside the otherwise-static page header.
 */
export const TELEMETRY_MASK = 'dd-privacy-mask fs-mask';

/**
 * Drop a subtree from the recording entirely, replacing it with a same-size placeholder
 * rather than censored text. Stronger than {@link TELEMETRY_MASK}; reserve it for content
 * that should leave no shape behind at all.
 */
export const TELEMETRY_EXCLUDE = 'dd-privacy-hidden fs-exclude';
